import { useState, useEffect } from "react";
import { Alert, Box, Stack, Text } from "@chakra-ui/react";
import { observer } from "mobx-react-lite";
import { Row } from "./Row";
import { scrollToItem } from "../../../../../shared/utils/scroll-utils";
import { List as LazyList, RowComponentProps } from "react-window";
import { ConflictWarning } from "../ConflictWarning";
import { useStores } from "../../../../../shared/stores/RootStore";
import { rowHeight } from "./constants";
import { SpreadsheetHeader } from "./SpreadsheetHeader";

const headerHeightMultiplier = 3;

const SpreadsheetBase: React.FC = () => {
  const { codingStore, craNodeStore, craWayStore } = useStores();
  const [anchorRow, setAnchorRow] = useState<string>();
  const selectedCraWay = craWayStore.getSelected()!;
  const nodes = craNodeStore.nodesByWayId.get(selectedCraWay.id) ?? [];
  const selectedNode = craNodeStore.getSelected();
  const nodeCodings = codingStore.getFromSelectedWay()!;
  const readOnly = !selectedCraWay.permissions.canCode;

  useEffect(() => {
    if (selectedNode && nodes) {
      scrollToItem(`row-${selectedNode.id!}`);
    }
    // eslint-disable-next-line
  }, [!!selectedNode]);

  const setRowSelected = (
    nodeId: string,
    value: boolean,
    shiftKey: boolean,
  ) => {
    if (shiftKey && anchorRow) {
      const [firstIndex, lastIndex] = [
        nodes.findIndex((node) => node.id === anchorRow),
        nodes.findIndex((node) => node.id === nodeId),
      ].sort((a, b) => a - b);
      const nodeIdsToSelect = nodes
        .slice(firstIndex, lastIndex + 1)
        .map((node) => node.id!);

      craNodeStore.setMultiSelectedIds(
        new Set([...craNodeStore.multiSelectedIds, ...nodeIdsToSelect]),
      );
    } else {
      const newSet = new Set(craNodeStore.multiSelectedIds);
      if (!value) {
        newSet.delete(nodeId);
      } else {
        newSet.add(nodeId);
      }

      craNodeStore.setMultiSelectedIds(newSet);
      setAnchorRow(nodeId);
    }
  };

  function RowComponent({ index, style }: RowComponentProps<{}>) {
    if (index < headerHeightMultiplier) {
      return <Box style={style}></Box>;
    }
    const node = nodes[index - headerHeightMultiplier];
    const nodeCoding = nodeCodings.get(node.id)!;

    return (
      <Row
        readOnly={readOnly}
        key={node.id}
        style={style}
        index={index - headerHeightMultiplier}
        codingEntry={nodeCoding}
        setRowSelected={setRowSelected}
      />
    );
  }

  return (
    <Stack flexGrow={1} overflow="auto" position="relative" fontSize={"xs"}>
      <ConflictWarning />
      {readOnly && (
        <Alert.Root status="info">
          <Alert.Indicator />
          <Text fontSize="sm">
            You do not have permissions to code. To change this, please speak to
            the route manager.
          </Text>
        </Alert.Root>
      )}
      <LazyList
        rowComponent={RowComponent}
        rowCount={nodes.length + headerHeightMultiplier}
        rowHeight={rowHeight}
        rowProps={{}}
      >
        <SpreadsheetHeader />
      </LazyList>
    </Stack>
  );
};

export const Spreadsheet = observer(SpreadsheetBase);
